import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { customerSwitchPhoneNumberPanel } from "./customerActions";
import { setSelectedPhoneNumber } from "./phoneNumberActions";
import { CUSTOMER_TYPE, FORM_ID } from "../../../../../constants";
import { OfferingCode, RootState } from "../../../../../models";
import { CustomerExistingSubscription } from "../../../../../models/state";
import { get as Ajax_get, post as Ajax_post } from "../../../app/Ajax";
import { addSubscriptionToCart, setActiveSubscription } from "../../../checkout/state/actions/cartActions";
import { actionTypes as RF_AT } from "../../../rapidform";
import { VALIDATION_FIELD } from "../../../rapidform/formValidation";
import {
    isServiceSubscription,
    isTeliaTryggSubscription,
    getSubscriptions,
} from "../../../checkout/state/selectors/subscriptionsSelectors";
import { getSelectedPhoneNumber } from "../../../checkout/state/selectors/phoneNumberSelectors";
import {
    API_URL_GET_EXTRA_SIM_PRICES,
    API_URL_RETAILER_ADD_EXISTING_SUBSCRIPTION_SKELETON,
    API_URL_RETAILER_SET_PREPAID_WITH_STARTPAKKE,
} from "../../api";
import * as AT from "../actionTypes";
import * as CHECKOUT_AT from "../../../checkout/state/actionTypes";
import { setValue } from "../actions";
import { getCurrentSubscriptionStep } from "../selectors/subscriptionStepsSelectors";
import { getNumberOfSubscriptionsInCart } from "../../../checkout/state/selectors/cartSelectors";
import { endNavigating } from "../../../navigation/state/actions";
import { isSubscriptionScope } from "../../utils";

const getParsedAddress = (address: string): { city?: string; street?: string; zip?: string } => {
    const lastCommaIndex = address.lastIndexOf(",");

    let street: string | undefined;
    let postalPart: string | undefined;

    if (lastCommaIndex === -1) {
        street = address.trim();
    } else {
        street = address.slice(0, lastCommaIndex).trim();
        postalPart = address.slice(lastCommaIndex + 1).trim();
    }

    let zip: string | undefined;
    let city: string | undefined;

    if (postalPart) {
        const postalMatch = /^(\d{4})\s*(.+)$/.exec(postalPart);

        if (postalMatch) {
            zip = postalMatch[1];
            city = postalMatch[2];
        } else if (/^\d{4}$/.test(postalPart)) {
            zip = postalPart;
        } else {
            city = postalPart;
        }
    }

    return { street, zip, city };
};

const toDisplayBirthDate = (birthDate?: string): string | undefined => {
    if (!birthDate) return;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
    if (!match) return;

    return `${match[3]}.${match[2]}.${match[1]}`;
};

const setProductUserFormValue = (
    dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
    field: string,
    value: string | undefined,
) => {
    if (!value) return;

    dispatch({
        type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_PRODUCT_USER),
        payload: {
            errorMessage: undefined,
            form: FORM_ID.CUSTOMER_PRODUCT_USER,
            inputName: `user-${field}`,
            inputValue: value,
            visible: true,
        },
    });
};

export const addExistingSubscriptionSkeletonToCart =
    ({
        phoneNumber,
        scope,
        offeringCode,
        cartItemId,
    }: {
        phoneNumber: string;
        scope: string;
        offeringCode?: string;
        cartItemId?: string;
    }): ThunkAction<Promise<{ cartItemId?: string }>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const promise = Ajax_post<{ cartItemId: string }>(
            {
                url: API_URL_RETAILER_ADD_EXISTING_SUBSCRIPTION_SKELETON,
                data: { phoneNumber, scope, offeringCode, cartItemId },
            },
            dispatch,
        );

        dispatch({
            type: CHECKOUT_AT.CHECKOUT_ADD_SUBSCRIPTION_TO_CART,
            payload: promise,
        });

        return promise;
    };

export const addSubscriptionChangeToCart =
    (offeringCode: OfferingCode): ThunkAction<Promise<string | undefined>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const quantity = getNumberOfSubscriptionsInCart(getState(), offeringCode) + 1;
        const result = await dispatch(addSubscriptionToCart(offeringCode, quantity));
        return result?.cartItemId;
    };

export const initializeExistingSubscriptionPrefill =
    (
        existingSubscriptionForChange: CustomerExistingSubscription,
        lookupPhoneNumber: string,
    ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const { offeringCode, bundleId: existingBundleId, changeScope } = existingSubscriptionForChange;
        const isSimScopeChange = changeScope === "sim";
        const isProductUserScopeChange = changeScope === "productUser";
        const isSubscriptionChangeScopeChange = changeScope === "subscriptionChange";
        if (!offeringCode) return;

        const state = getState();
        const currentSubscriptions = getSubscriptions(state);
        let subscriptionId: string | undefined =
            existingBundleId && currentSubscriptions[existingBundleId] ? existingBundleId : undefined;

        if (!subscriptionId) {
            subscriptionId = Object.keys(currentSubscriptions).find(
                (currentSubscriptionId) => getSelectedPhoneNumber(state, currentSubscriptionId) === lookupPhoneNumber,
            );
        }

        if (!subscriptionId && isSubscriptionChangeScopeChange) {
            dispatch(setIsAddingSubscription(true));
            return;
        }

        if (!subscriptionId) {
            if (isSubscriptionScope(changeScope)) {
                const addResult = await dispatch(
                    addSubscriptionToCart(offeringCode, getNumberOfSubscriptionsInCart(getState(), offeringCode) + 1),
                );
                subscriptionId = addResult?.cartItemId;
            } else {
                const addResult = await dispatch(
                    addExistingSubscriptionSkeletonToCart({ phoneNumber: lookupPhoneNumber, scope: changeScope! }),
                );
                subscriptionId = addResult?.cartItemId;
            }
        }

        if (!subscriptionId) return;

        const updatedSubscriptions = getSubscriptions(getState());
        let initialStepIndex = 0;
        if (isProductUserScopeChange) {
            initialStepIndex = 2;
        } else if (isSimScopeChange) {
            initialStepIndex = 1;
        } else if (isSubscriptionChangeScopeChange) {
            initialStepIndex = 0;
        }

        dispatch(setIsAddingSubscription(false));
        dispatch(setValue("isCatalogListingOk", updatedSubscriptions[subscriptionId]?.isCatalogListingOk ?? true));
        dispatch(setSelectedPhoneNumber(lookupPhoneNumber, CUSTOMER_TYPE.EXISTING, subscriptionId, true));

        let stepsToComplete = 1;

        if (existingSubscriptionForChange.phoneNumber) {
            stepsToComplete = 2;
        }

        if (isSimScopeChange) {
            stepsToComplete = 2;
        }

        setProductUserFormValue(dispatch, VALIDATION_FIELD.FIRST_NAME, existingSubscriptionForChange.firstName);
        setProductUserFormValue(dispatch, VALIDATION_FIELD.LAST_NAME, existingSubscriptionForChange.lastName);

        const formattedBirthDate = toDisplayBirthDate(existingSubscriptionForChange.birthDate);
        setProductUserFormValue(dispatch, VALIDATION_FIELD.BIRTHDATE, formattedBirthDate);

        if (existingSubscriptionForChange.address) {
            const { street, zip, city } = getParsedAddress(existingSubscriptionForChange.address);
            setProductUserFormValue(dispatch, VALIDATION_FIELD.STREET, street);
            setProductUserFormValue(dispatch, VALIDATION_FIELD.POSTCODE, zip);
            setProductUserFormValue(dispatch, VALIDATION_FIELD.CITY, city);
        }

        if (existingSubscriptionForChange.email) {
            setProductUserFormValue(dispatch, VALIDATION_FIELD.PRODUCT_USER_EMAIL, existingSubscriptionForChange.email);
            dispatch(setValue("isProductUserDifferentThanLegalOwner", true));
            stepsToComplete = 3;
        }

        if (isProductUserScopeChange) {
            stepsToComplete = 3;
        }

        dispatch(setCompletedSubscriptionSteps(subscriptionId, stepsToComplete));

        if (isSubscriptionScope(changeScope)) {
            dispatch(resetSubscriptionSteps(subscriptionId));
            dispatch(setActiveSubscription(subscriptionId));
            dispatch(setSubscriptionStep(subscriptionId, 0));
        }

        dispatch(setActiveSubscription(subscriptionId));
        dispatch(setSubscriptionStep(subscriptionId, initialStepIndex));
    };

export const setSubscriptionStep =
    (subscriptionId: string, stepIndex: number): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: AT.RETAILER_SET_SUBSCRIPTION_STEP,
            payload: { subscriptionId, stepIndex },
        });
    };

export const resetSubscriptionSteps =
    (subscriptionId: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: AT.RETAILER_RESET_SUBSCRIPTION_STEPS,
            payload: { subscriptionId },
        });
    };

export const setCompletedSubscriptionSteps =
    (subscriptionId: string, completedSteps: number): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: AT.RETAILER_SET_COMPLETED_SUBSCRIPTION_STEPS,
            payload: { subscriptionId, completedSteps },
        });
    };

export const setExistingSubscriptionForChange =
    (
        subscription: CustomerExistingSubscription | undefined,
    ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response = Promise.resolve(subscription);

        dispatch({
            type: AT.RETAILER_SET_EXISTING_SUBSCRIPTION_FOR_CHANGE,
            payload: response,
        });
    };

export const clearExistingSubscriptionForChange =
    (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) => {
        dispatch({
            type: AT.RETAILER_CLEAR_EXISTING_SUBSCRIPTION_FOR_CHANGE,
        });
    };

export const setReopenExistingSubscriptionPhone =
    (phoneNumber: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: AT.RETAILER_SET_REOPEN_EXISTING_SUBSCRIPTION_PHONE,
            payload: { phoneNumber },
        });
    };

export const clearReopenExistingSubscriptionPhone =
    (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) => {
        dispatch({
            type: AT.RETAILER_CLEAR_REOPEN_EXISTING_SUBSCRIPTION_PHONE,
        });
    };

const shouldSkipPhoneNumberStep = (state: RootState, subscriptionId: string): boolean =>
    isTeliaTryggSubscription(state, subscriptionId) || isServiceSubscription(state, subscriptionId);

export const nextSubscriptionStep =
    (subscriptionId: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const currentStep = getCurrentSubscriptionStep(state, subscriptionId);
        if (shouldSkipPhoneNumberStep(state, subscriptionId) && currentStep === 0) {
            if (isServiceSubscription(state, subscriptionId)) {
                dispatch(customerSwitchPhoneNumberPanel(CUSTOMER_TYPE.NEW, subscriptionId));
            }
            dispatch({
                type: AT.RETAILER_SET_SUBSCRIPTION_STEP,
                payload: { subscriptionId, stepIndex: 2 },
            });
        } else {
            dispatch({
                type: AT.RETAILER_NEXT_SUBSCRIPTION_STEP,
                payload: { subscriptionId },
            });
        }
        dispatch(endNavigating(""));
    };

export const prevSubscriptionStep =
    (subscriptionId: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const currentStep = getCurrentSubscriptionStep(state, subscriptionId);
        if (shouldSkipPhoneNumberStep(state, subscriptionId) && currentStep === 2) {
            dispatch({
                type: AT.RETAILER_SET_SUBSCRIPTION_STEP,
                payload: { subscriptionId, stepIndex: 0 },
            });
        } else {
            dispatch({
                type: AT.RETAILER_PREV_SUBSCRIPTION_STEP,
                payload: { subscriptionId },
            });
        }
        dispatch(endNavigating(""));
    };

export const getExtraSimPrices =
    (memberId = "", phoneNumber = ""): ThunkAction<Promise<string[]>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response: Promise<string[]> = Ajax_get(
            {
                url: API_URL_GET_EXTRA_SIM_PRICES(memberId, phoneNumber),
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_FETCH_EXTRA_SIM_PRICES,
            payload: response,
            meta: { memberId },
        });

        return response;
    };

export const setIsAddingSubscription =
    (isAddingSubscription: boolean): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: AT.RETAILER_SET_IS_ADDING_SUBSCRIPTION,
            payload: { isAddingSubscription },
        });
    };

export const setPrepaidWithStartpakke =
    (subscriptionId: string, isPrepaidWithStartpakke: boolean): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: AT.RETAILER_SET_PREPAID_WITH_STARTPAKKE,
            payload: Ajax_post(
                {
                    url: API_URL_RETAILER_SET_PREPAID_WITH_STARTPAKKE,
                    data: { subscriptionId, isPrepaidWithStartpakke },
                },
                dispatch,
            ),
            meta: { subscriptionId, isPrepaidWithStartpakke },
        });
    };
